import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../config';
import type { HabitPreset, HabitCategory, HabitFrequencyConfig } from '@/types/habits';

/**
 * Get all active habit presets
 */
export async function getHabitPresets(): Promise<HabitPreset[]> {
  try {
    const presetsQuery = query(
      collection(db, 'habitPresets'),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(presetsQuery);
    
    const presets = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as HabitPreset[];
    
    // Sort in memory instead of Firestore to avoid index requirement
    return presets.sort((a, b) => {
      const categoryCompare = a.category.localeCompare(b.category);
      if (categoryCompare !== 0) return categoryCompare;
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    console.error('Error getting habit presets:', error);
    return [];
  }
}

/**
 * Get all habit presets (including inactive) - Admin only
 */
export async function getAllHabitPresets(): Promise<HabitPreset[]> {
  try {
    const presetsQuery = query(collection(db, 'habitPresets'));
    
    const snapshot = await getDocs(presetsQuery);
    
    const presets = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as HabitPreset[];
    
    // Sort in memory instead of Firestore to avoid index requirement
    return presets.sort((a, b) => {
      const categoryCompare = a.category.localeCompare(b.category);
      if (categoryCompare !== 0) return categoryCompare;
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    console.error('Error getting all habit presets:', error);
    return [];
  }
}

/**
 * Create a new habit preset
 */
export async function createHabitPreset(data: {
  name: string;
  category: HabitCategory;
  createdBy: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  isActive?: boolean;
  frequencyConfig?: HabitFrequencyConfig;
}): Promise<string> {
  try {
    const now = Timestamp.now();
    const presetData = {
      name: data.name,
      nameAr: data.nameAr || '',
      category: data.category,
      description: data.description || '',
      descriptionAr: data.descriptionAr || '',
      isActive: data.isActive !== undefined ? data.isActive : true,
      createdAt: now,
      updatedAt: now,
      createdBy: data.createdBy,
      frequencyConfig: data.frequencyConfig || {
        frequency: 'daily' as const,
        timesPerFrequency: 1,
        duration: 90,
      }
    };

    const docRef = await addDoc(collection(db, 'habitPresets'), presetData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating habit preset:', error);
    throw error;
  }
}

/**
 * Update a habit preset
 */
export async function updateHabitPreset(
  presetId: string,
  updates: Partial<Omit<HabitPreset, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  try {
    const presetRef = doc(db, 'habitPresets', presetId);
    await updateDoc(presetRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating habit preset:', error);
    throw error;
  }
}

/**
 * Delete a habit preset
 */
export async function deleteHabitPreset(presetId: string): Promise<void> {
  try {
    const presetRef = doc(db, 'habitPresets', presetId);
    await deleteDoc(presetRef);
  } catch (error) {
    console.error('Error deleting habit preset:', error);
    throw error;
  }
}

/**
 * Toggle preset active status
 */
export async function toggleHabitPresetStatus(presetId: string, isActive: boolean): Promise<void> {
  try {
    const presetRef = doc(db, 'habitPresets', presetId);
    await updateDoc(presetRef, {
      isActive,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error toggling habit preset status:', error);
    throw error;
  }
}

